import { Heart, Sparkles, BookOpen, Cake, Camera, Clock, Users, Star } from "lucide-react";
import { SITE, IMAGES } from "../data/site";
import { PageHero, Reveal, SectionHeading, CallButton, PartyButton } from "../components/Shared";

const values = [
  { icon: Clock, text: "Slow down" },
  { icon: Heart, text: "Have fun" },
  { icon: Sparkles, text: "Use their imagination" },
  { icon: BookOpen, text: "Read" },
  { icon: Cake, text: "Celebrate" },
  { icon: Camera, text: "Take pictures" },
  { icon: Users, text: "Spend time together" },
  { icon: Star, text: "Make lasting memories" },
];

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="Our story"
        title="Welcome to Ms. Jeanette's"
        sub="A little sweetness, a little imagination, and a whole lot of memories."
        testid="about-hero"
      >
        <CallButton testid="about-hero-call-btn" />
      </PageHero>

      <section className="px-4 py-20 sm:px-6" data-testid="about-owner-section">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <img
                src={IMAGES.stand.url}
                alt={IMAGES.stand.alt}
                loading="lazy"
                className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl shadow-blue-500/15"
                data-testid="about-owner-image"
              />
              <div className="absolute -bottom-5 left-6 rounded-2xl bg-yellow-400 px-5 py-3 shadow-lg shadow-yellow-500/30">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-900">The little green stand</p>
                <p className="font-display text-lg font-semibold text-slate-900" data-testid="about-stand-badge">Est. June 25, 2026</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="text-3xl font-semibold sm:text-4xl">A Place Made for Families</h2>
            <p className="mt-5 text-base text-slate-600 md:text-lg">
              Ms. Jeanette's Shaved Ice Snowballs opened its doors on{" "}
              <strong className="text-slate-800">{SITE.established}</strong>, created by{" "}
              <strong className="text-slate-800">{SITE.owner}</strong> with one simple mission:
            </p>
            <blockquote className="mt-5 rounded-3xl border-l-8 border-yellow-400 bg-yellow-50 p-6 text-base font-bold text-slate-700 md:text-lg" data-testid="about-mission-quote">
              "A little sweetness, a little imagination, and a whole lot of memories. At Ms. Jeanette's, our
              mission is to create a welcoming place where children and families can slow down, have fun,
              learn, celebrate, and make lasting memories."
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section className="bg-blue-50 px-4 py-20 sm:px-6" data-testid="about-values-section">
        <SectionHeading
          eyebrow="Why we're here"
          title="A Place Where Children and Families Can…"
          testid="about-values-heading"
        />
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {values.map((value, i) => {
            const Icon = value.icon;
            return (
              <Reveal key={value.text} delay={i * 0.05}>
                <div className="flex h-full flex-col items-center gap-3 rounded-3xl bg-white p-6 text-center shadow-lg shadow-blue-500/10 transition-transform duration-200 hover:-translate-y-1.5" data-testid={`about-value-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                    <Icon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                  </span>
                  <span className="text-sm font-extrabold text-slate-800">{value.text}</span>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="px-4 py-20 text-center sm:px-6" data-testid="about-cta-section">
        <Reveal>
          <h2 className="text-3xl font-semibold sm:text-4xl">Come Say Hello</h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-600 md:text-lg">
            Ms. Jeanette would love to welcome your family. Stop by for a snowball, or give her a call to
            plan something special.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <CallButton testid="about-cta-call-btn" />
            <PartyButton testid="about-cta-party-btn" />
          </div>
        </Reveal>
      </section>
    </>
  );
}
